import {
  Link,
  useNavigate
} from "react-router-dom";
import {
  Container,
  Row,
  Card,
  Breadcrumb,
  Col
} from "react-bootstrap";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page as Paging,
  PageSettingsModel,
  Filter,
  Resize,
  FilterSettingsModel
} from "@syncfusion/ej2-react-grids";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider
} from "@whitespace/whitespace-ui";
import { useEffect, useState, useCallback } from "react";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import { parseUsingParseInt } from "helpers/stringUtils";
import IHwrcSite from "interfaces/IHwrcSite";
import mapLink from "assets/map-link.png";
import "styles/sf-datagrid.scss";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { SourceEnum } from "modules/sourceEnum";
import EditTemplate from "./Templates/EditTemplate";

const pageSize: number = parseUsingParseInt(process.env.REACT_APP_PAGE_SIZE);
const pageSettings: PageSettingsModel = { pageSize, pageSizes: [5, 10, 20, 50, "All"] };
const filterSettings: FilterSettingsModel = { type: "Excel" };
let grid: GridComponent | undefined;

const activeTemplate = (props: any): any => (props.active ? "Active" : "Inactive");
const activeTemplateInstance = activeTemplate;

const mapViewTemplate = (props: any): any => {
  const { latitude, longitude, id } = props;
  const linkToMaps = `${RoutePathsEnum.Map}/${SourceEnum.WorkOrders}/${SourceEnum.HwrcSite}/${id}`;

  return (
    <div className="d-flex justify-content-center align-items-center col-map-view overflow-auto">
      {latitude && longitude
    && (
      <>
        <img src={mapLink} alt="Pin Icon" />
        <Link to={linkToMaps} data-testid="view-map">View</Link>
      </>
    ) }
      {!(latitude && longitude)
        && <span className="map-link-error">Error</span>}

    </div>
  );
};

const mapViewTemplateInstance = mapViewTemplate;

const ManageHwrcSites = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [renderingComplete, setRenderingComplete] = useState(false);
  const {
    isSending,
    errors,
    data,
    statusCode,
    sendRequest
  } = useAmplifyREST();
  const navigate = useNavigate();

  const [
    hwrcSites,
    setHwrcSites
  ] = useState<IHwrcSite[] | undefined>(undefined);

  useEffect(() => {
    sendRequest("settings/hwrcsites", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (data && data.hwrcSites) setHwrcSites(data.hwrcSites);
  }, [data, setHwrcSites]);

  useEffect(() => {
    if (errors) setShowExceptions(true);
  }, [errors]);

  useEffect(() => {
    if (![0, 200].includes(statusCode)) {
      setRenderingComplete(true);
    }
  }, [statusCode]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const isProcessing = isSending || !renderingComplete;

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="region-management">
              Site Management
              <Spinner
                animation="grow"
                visible={isProcessing}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
          <div className="float-end">
            <IconButton
              data-testid="add-hwrcsite"
              iconType={IconButtonType.Add}
              variant="outline-primary btn-create"
              onClick={() => navigate(RoutePathsEnum.SettingsHwrcSitesAdd)}
            >
              Add New Site
            </IconButton>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={8} className="d-flex">
              <Breadcrumb className="left-margin-large no-wrap">
                <Breadcrumb.Item
                  data-testid="breadcrumb-settings"
                  linkAs={Link}
                  linkProps={{ to: RoutePathsEnum.Settings }}
                  onClick={() => navigate(RoutePathsEnum.Settings)}
                >
                  Settings
                </Breadcrumb.Item>
                <Breadcrumb.Item active data-testid="breadcrumb-hwrcsites">HWRC Sites</Breadcrumb.Item>
              </Breadcrumb>
            </Col>
          </Row>
          <Divider />
          <section className="pt-2">
            <Container fluid>
              <Row>
                <div className="control-pane">
                  <div className="control-section" data-testid="grid">

                    <GridComponent
                      ref={(g:GridComponent) => { grid = g; }}
                      id="hwrcSitesGrid"
                      data-testid="hwrcSitesGrid"
                      allowPaging
                      allowResizing
                      pageSettings={pageSettings}
                      allowSorting
                      allowFiltering
                      filterSettings={filterSettings}
                      allowSelection={false}
                      dataSource={hwrcSites}
                      actionComplete={() => { makeGridAACompliant(grid!, () => { setRenderingComplete(true); }); }}
                      loadingIndicator={{ indicatorType: "Shimmer" }}
                      type=""
                    >
                      <ColumnsDirective>
                        <ColumnDirective
                          field="id"
                          headerText="id"
                          visible={false}
                          isPrimaryKey
                        />
                        <ColumnDirective
                          field="name"
                          headerText="Name"
                        />
                        <ColumnDirective
                          field="regionName"
                          headerText="Region Name"
                          hideAtMedia="(min-width:800px)"
                        />
                        <ColumnDirective
                          field="code"
                          headerText="Site Code"
                          width={50}
                          hideAtMedia="(min-width:1200px)"
                        />
                        <ColumnDirective
                          field="active"
                          headerText="Status"
                          width={50}
                          template={activeTemplateInstance}
                          hideAtMedia="(min-width:1200px)"
                        />
                        <ColumnDirective
                          headerText=""
                          width={50}
                          allowSorting={false}
                          allowResizing={false}
                          template={mapViewTemplateInstance}
                          allowFiltering={false}
                          hideAtMedia="(min-width:1200px)"
                        />
                        <ColumnDirective
                          headerText=""
                          width={50}
                          allowSorting={false}
                          allowResizing={false}
                          template={EditTemplate}
                          allowFiltering={false}
                        />
                      </ColumnsDirective>
                      <Inject services={[Filter, Paging, Sort, Resize]} />
                    </GridComponent>

                  </div>

                </div>
              </Row>
            </Container>
          </section>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default ManageHwrcSites;
